'use client'

import {
  ChevronDown,
  ChevronUp,
  InfoIcon,
  KeyRoundIcon,
  LockIcon,
  RotateCcwIcon,
  SparklesIcon,
} from 'lucide-react'
import Button from '@shared/components/Button/Button'
import { useState, useTransition } from 'react'
import { summarizeAction } from './summarizeAction'
import type { SummaryResponse } from '@shared/lib/judiciaryMonitoring/judiciaryMonitoring'
import { Row, Stack } from '@christiankaindl/lyts'
import Text from '@shared/components/Text/Text'
import Spinner from '@shared/components/Spinner/Spinner'
import { card } from '@/styles/theme.css'
import Pill from '@shared/components/Pill/Pill'
import RefButton from '@shared/components/Legal/RefButton'
import Heading from '@shared/components/Heading/Heading'
import {
  Disclosure,
  DisclosureContent,
  useDisclosureStore,
} from '@ariakit/react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shared/components/Tooltip/Tooltip'
import { useAuth } from '@clerk/nextjs'
import { useLoginHref } from '../NavBar/Auth'

const BRAND = '#A48757'
const lockTooltip = 'Abonnieren, um die KI-Zusammenfassung zu lesen'

type SummaryPreview = Pick<SummaryResponse, 'title' | 'keywords'>

type Props = {
  id: string
  preview?: SummaryPreview
}

export default function SummarizeButton({
  id,
  preview: initialPreview,
}: Props) {
  const auth = useAuth()
  const loginHref = useLoginHref()
  const disclosure = useDisclosureStore()
  const isOpen = disclosure.useState('open')
  const [isPending, startTransition] = useTransition()
  const [summary, setSummary] = useState<SummaryResponse | null>(null)
  const [isError, setIsError] = useState(false)
  const preview = summary ?? initialPreview

  // Treat unloaded auth as "not signed in" so SSR doesn't expose protected
  // content; once Clerk loads, the locked UI swaps for the interactive one.
  const locked = !(auth.isLoaded && auth.isSignedIn === true)

  function handleClick() {
    startTransition(async () => {
      try {
        setIsError(false)
        const token = await auth.getToken()
        if (!token) {
          throw new Error('Unauthorized')
        }
        const result = await summarizeAction(id, token)
        if (!result.success) {
          throw new Error(result.reason)
        }
        disclosure.setOpen(true)
        setSummary(result.summary)
      } catch (error) {
        console.error(error)
        setIsError(true)
      }
    })
  }

  return (
    <Stack style={{ marginLeft: -12 }}>
      {!preview?.title && (
        <Stack className={card} gap={0.25} style={{ padding: '0.875em 1em' }}>
          <Row gap={1} xAlign='space-between' yAlign='center' wrap>
            <Stack gap={0.25}>
              <Row gap={0.5} yAlign='center'>
                <SparklesIcon size={16} style={{ color: BRAND }} />
                <Text style={{ color: BRAND, fontWeight: 500 }}>
                  Codara <span style={{ fontWeight: 300 }}>Summary</span>
                </Text>
                <AiInfoTooltip size={14} />
              </Row>
              <Text size='small' color='secondary'>
                Sachverhalt, Spruch und rechtliche Beurteilung – kompakt
                zusammengefasst.
              </Text>
            </Stack>
            {locked ? (
              <Button
                href={loginHref}
                tooltip={lockTooltip}
                variant='brand'
                size='small'
                circular
              >
                Abonnieren <KeyRoundIcon size={16} />
              </Button>
            ) : (
              <Button
                variant='brand'
                size='small'
                circular
                disabled={isPending}
                onClick={handleClick}
                // @ts-expect-error Spinner is not a LucideIcon but matches the runtime contract
                icon={isPending ? Spinner : SparklesIcon}
              >
                {isPending ? 'Erstelle…' : 'Erstellen'}
              </Button>
            )}
          </Row>
        </Stack>
      )}

      {isError && (
        <Row gap={0.5} yAlign='center'>
          <Text>Ein Fehler ist aufgetreten.</Text>
          <Button
            size='small'
            variant='flat'
            disabled={isPending}
            onClick={handleClick}
            // @ts-expect-error Spinner is not a LucideIcon but matches the runtime contract
            icon={isPending ? Spinner : RotateCcwIcon}
          >
            Erneut versuchen
          </Button>
        </Row>
      )}

      {preview?.title && (
        <Stack
          gap={1.25}
          className={card}
          style={{ paddingTop: 0, paddingBottom: 0 }}
        >
          {locked ? (
            <Row
              xAlign='space-between'
              yAlign='start'
              style={{ padding: '1em 0' }}
            >
              <SummaryHeader summary={preview} />
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      circular
                      square
                      href={loginHref}
                      aria-label={lockTooltip}
                    >
                      <LockIcon size={18} />
                    </Button>
                  }
                />
                <TooltipContent>{lockTooltip}</TooltipContent>
              </Tooltip>
            </Row>
          ) : (
            <>
              <Disclosure
                render={<Row xAlign='space-between' yAlign='start' />}
                store={disclosure}
                onClick={() => {
                  if (!summary && !isPending) handleClick()
                }}
                style={{ padding: '1em 0', cursor: 'pointer' }}
              >
                <SummaryHeader summary={preview} />
                <Button circular square disabled={isPending}>
                  {isPending ? (
                    <Spinner />
                  ) : isOpen ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </Button>
              </Disclosure>
              <DisclosureContent
                render={<Stack />}
                store={disclosure}
                unmountOnHide
              >
                {summary?.facts && (
                  <Text>
                    <b>Sachverhalt:</b> {summary.facts}
                  </Text>
                )}
                {summary?.verdict && (
                  <Text>
                    <b>Spruch:</b> {summary.verdict}
                  </Text>
                )}
                {summary?.legalAssessment && (
                  <Text>
                    <b>Rechtliche Beurteilung:</b> {summary.legalAssessment}
                  </Text>
                )}
                {summary?.refs && summary.refs.length > 0 && (
                  <Row gap={0.5} wrap style={{ marginBottom: 15 }}>
                    {summary.refs.map((ref) => (
                      <RefButton key={ref.id} {...ref} id={ref.citation} />
                    ))}
                  </Row>
                )}
              </DisclosureContent>
            </>
          )}
        </Stack>
      )}
    </Stack>
  )
}

function SummaryHeader({ summary }: { summary: SummaryPreview }) {
  return (
    <Stack gap={0.75}>
      <Row gap={0} style={{ margin: '-3px 0px -9px -9px' }}>
        <Button
          disabled
          style={{ color: BRAND, opacity: 1 }}
          variant='flat'
          size='small'
          icon={SparklesIcon}
        >
          Codara <span style={{ fontWeight: 300 }}>Summary</span>
        </Button>
        <AiInfoTooltip size={16} />
      </Row>
      {summary.title && <Heading h={5}>{summary.title}</Heading>}
      {summary.keywords && summary.keywords.length > 0 && (
        <Row wrap gap={0.5}>
          {summary.keywords.map((keyword) => {
            return <Pill key={keyword}>{keyword}</Pill>
          })}
        </Row>
      )}
    </Stack>
  )
}

function AiInfoTooltip({ size }: { size: number }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={<InfoIcon size={size} />}
        style={{ opacity: 0.4 }}
      />
      <TooltipContent>Das Summary ist KI-generiert</TooltipContent>
    </Tooltip>
  )
}
